import { Card, Table, Tag } from 'antd';
import { PageShell, RefreshButton } from '../components/page-shell.js';
import { useAdminQuery } from '../use-admin-query.js';

type ValidationResponse = {
  from: string;
  groups: Array<{
    productId: string;
    operationKey: string;
    validator: string;
    outcome: string;
    _count: { _all: number };
    _avg: { durationMs?: number };
  }>;
  recent: Array<{
    id: string;
    product: { key: string };
    jobId?: string;
    operationKey: string;
    stage: string;
    validator: string;
    outcome: string;
    durationMs?: number;
    errorCode?: string;
    createdAt: string;
  }>;
};

export function ValidationPage() {
  const query = useAdminQuery<ValidationResponse>('/admin/v1/validations');
  return (
    <PageShell
      title="Product validation"
      description="Aggregated product-owned validation outcomes. Metadata is bounded and prohibited from containing prompts or responses."
      action={
        <RefreshButton
          loading={query.loading}
          onClick={() => void query.refresh()}
        />
      }
      error={query.error}
    >
      <Card title="30-day outcome groups" className="section-card">
        <Table
          rowKey={(row) =>
            `${row.productId}:${row.operationKey}:${row.validator}:${row.outcome}`
          }
          loading={query.loading}
          dataSource={query.data?.groups ?? []}
          columns={[
            { title: 'Operation', dataIndex: 'operationKey' },
            { title: 'Validator', dataIndex: 'validator' },
            {
              title: 'Outcome',
              dataIndex: 'outcome',
              render: (value: string) => (
                <Tag color={value === 'PASSED' ? 'green' : 'red'}>{value}</Tag>
              ),
            },
            { title: 'Count', render: (_, row) => row._count._all },
            {
              title: 'Average duration',
              render: (_, row) =>
                row._avg.durationMs === undefined
                  ? '—'
                  : `${Math.round(row._avg.durationMs)} ms`,
            },
          ]}
        />
      </Card>
      <Card
        title="Recent unavailable or failed validations"
        className="section-card"
      >
        <Table
          rowKey="id"
          dataSource={query.data?.recent ?? []}
          columns={[
            { title: 'Created', dataIndex: 'createdAt', render: localTime },
            { title: 'Product', render: (_, row) => row.product.key },
            { title: 'Operation', dataIndex: 'operationKey' },
            { title: 'Stage', dataIndex: 'stage' },
            { title: 'Validator', dataIndex: 'validator' },
            { title: 'Outcome', dataIndex: 'outcome' },
            { title: 'Error', dataIndex: 'errorCode', render: dash },
          ]}
        />
      </Card>
    </PageShell>
  );
}

function localTime(value: string): string {
  return new Date(value).toLocaleString();
}

function dash(value: unknown): string {
  return typeof value === 'string' && value ? value : '—';
}
